import { AuthContext } from '../../context/AuthContext';
import { useLang } from '../../context/LangContext';
import { useEffect, useContext, useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Welcome from '../../components/Header/Welcome';
import { AdminNav } from '../../components';

const Destinations = () => {
  const [destinations, setDestinations] = useState([]);
  const { token, currentUser } = useContext(AuthContext);
  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('city');
  const [newUrl, setNewUrl] = useState('');
  const [newYear, setNewYear] = useState('');
  const [newSemesters, setNewSemesters] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [idToUpdate, setIdToUpdate] = useState('');
  const [currentSlug, setCurrentSlug] = useState('');

  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [newCountryId, setNewCountryId] = useState('');
  const [citySearch, setCitySearch] = useState('');

  const [isUpdate, setIsUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getDestinations = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations`
      );
      setDestinations(response.data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCountries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/countries`
      );
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const getRegionsAndCities = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/regions`);
      setRegions(response.data.regions || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const handleSave = async () => {
    if (!newName || !newType) return alert('Nombre y tipo son requeridos');
    if (newType === 'country' && !newCountryId) return alert('Debes seleccionar un país');
    if (newSemesters.length === 0) return alert('Debes seleccionar al menos un semestre');

    try {
      for (const sem of newSemesters) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations`,
          {
            name: newName,
            type: newType,
            whatsapp_url: newUrl,
            year: newYear,
            semester: sem,
            cityIds: newType === 'city' ? selectedCities : [],
            country_id: newType === 'country' ? newCountryId : null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      handleCloseModal();
      getDestinations();
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = dest => {
    setIsUpdate(true);
    setIdToUpdate(dest.id);
    setNewName(dest.name);
    setNewType(dest.type);
    setNewUrl(dest.whatsapp_url || '');
    setNewYear(dest.year || '');
    setNewSemesters(dest.semester ? [dest.semester.toString()] : []);
    setSelectedCities(dest.cities ? dest.cities.map(c => c.id) : []);
    setNewCountryId(dest.country_id || dest.country?.id || '');
    setCurrentSlug(dest.slug || '');
    setShowModal(true);
  };

  const handleUpdate = async () => {
    if (newType === 'country' && !newCountryId) return alert('Debes seleccionar un país');
    if (newSemesters.length === 0) return alert('Debes seleccionar al menos un semestre');

    try {
      // Update the current record with the first selected semester
      const primarySem = newSemesters[0];
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations/${idToUpdate}`,
        {
          name: newName,
          type: newType,
          whatsapp_url: newUrl,
          year: newYear,
          semester: primarySem,
          cityIds: newType === 'city' ? selectedCities : [],
          country_id: newType === 'country' ? newCountryId : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If they selected more than one semester during edit, create new records for the rest
      if (newSemesters.length > 1) {
        for (let i = 1; i < newSemesters.length; i++) {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations`,
            {
              name: newName,
              type: newType,
              whatsapp_url: newUrl,
              year: newYear,
              semester: newSemesters[i],
              cityIds: newType === 'city' ? selectedCities : [],
              country_id: newType === 'country' ? newCountryId : null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      }

      handleCloseModal();
      getDestinations();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm(texts.admin_common.delete_confirm)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/${lang}/destinations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      getDestinations();
    } catch (error) {
      console.error(error);
    }
  };

  const resetForm = () => {
    setIsUpdate(false);
    setIdToUpdate('');
    setNewName('');
    setNewType('city');
    setNewUrl('');
    setNewYear('');
    setNewSemesters([]);
    setSelectedCities([]);
    setNewCountryId('');
    setCitySearch('');
    setCurrentSlug('');
  };

  const allCities = useMemo(() => {
    return regions.flatMap(r => r.cities.map(c => ({ ...c, regionName: r.name })));
  }, [regions]);

  const availableCities = allCities.filter(
    c => !selectedCities.includes(c.id) && c.name.toLowerCase().includes(citySearch.toLowerCase())
  );
  const selectedCitiesObjects = selectedCities
    .map(id => allCities.find(c => c.id === id))
    .filter(Boolean);

  const toggleSemester = val => {
    setNewSemesters(prev => (prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]));
  };

  useEffect(() => {
    if (!token) {
      window.location.href = '/';
    } else {
      getDestinations();
      getCountries();
      getRegionsAndCities();
    }
  }, [token]);

  if (currentUser && !currentUser.isAdmin) {
    return <Navigate to="/admin" />;
  }

  return (
    <>
      <Welcome />
      <div className="container py-4 mb-5" style={{ minHeight: '80vh' }}>
        <AdminNav />

        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-sen mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-map-marker"></i>
              <span>
                {texts.admin_common.list_of} {texts.admin.menu_destinations}
              </span>
            </h2>
            <span className="text-muted small">
              {lang === 'es'
                ? 'Administra los destinos y grupos de WhatsApp por país, ciudad, año y semestre.'
                : 'Manage WhatsApp group destinations by country, city, year, and semester.'}
            </span>
          </div>
          <div className="mt-3 mt-sm-0">
            <button
              className="btn btn-primary bg-sen border-0 shadow-sm px-4 py-2 rounded-3 fw-bold d-flex align-items-center gap-2"
              onClick={handleOpenModal}
            >
              <i className="fa fa-plus-circle"></i>
              <span>
                {texts.admin_common.new} {texts.admin.menu_destinations}
              </span>
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>{texts.admin_common.name}</th>
                    <th>{texts.admin_common.type}</th>
                    <th>Año</th>
                    <th>Semestre</th>
                    <th>WhatsApp URL</th>
                    <th className="text-end">{texts.admin_common.options}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status" />
                      </td>
                    </tr>
                  ) : (
                    destinations.map(dest => (
                      <tr key={dest.id}>
                        <td className="fw-bold">{dest.name}</td>
                        <td>
                          {dest.type === 'city' ? (
                            <span className="badge bg-secondary">Ciudad de Chile</span>
                          ) : (
                            <span className="badge bg-primary">
                              {dest.country ? `País: ${dest.country.name}` : 'País'}
                            </span>
                          )}
                        </td>
                        <td>
                          {dest.year ? (
                            <span className="badge bg-secondary">{dest.year}</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {dest.semester ? (
                            <span className="badge bg-info">
                              {dest.semester === 1
                                ? texts.wizard?.semester_1_short || 'Sem. 1 (Marzo - Julio)'
                                : dest.semester === 2
                                  ? texts.wizard?.semester_2_short || 'Sem. 2 (Agosto - Diciembre)'
                                  : `Sem. ${dest.semester}`}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: '250px' }}>
                          {dest.whatsapp_url ? (
                            <a
                              href={dest.whatsapp_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-decoration-none"
                            >
                              <i className="fa fa-link me-1"></i> {dest.whatsapp_url}
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(dest)}
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(dest.id)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                  {!isLoading && destinations.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        <i className="fa fa-inbox fa-3x mb-3 d-block"></i>
                        {texts.admin_common.no_records}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            {isUpdate ? texts.admin_common.edit : texts.admin_common.new}{' '}
            {texts.admin.menu_destinations}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-3">
            <label className="fw-semibold mb-2">{texts.admin_common.type}</label>
            <div className="d-flex gap-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="destType"
                  id="typeCity"
                  value="city"
                  checked={newType === 'city'}
                  onChange={e => setNewType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="typeCity">
                  Ciudad de Chile
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="destType"
                  id="typeCountry"
                  value="country"
                  checked={newType === 'country'}
                  onChange={e => setNewType(e.target.value)}
                />
                <label className="form-check-label" htmlFor="typeCountry">
                  País
                </label>
              </div>
            </div>
          </div>

          {newType === 'country' ? (
            <div className="form-group mb-3">
              <label className="fw-semibold mb-2">País asociado al destino</label>
              <select
                className="form-select"
                value={newCountryId}
                onChange={e => {
                  const selectedId = e.target.value;
                  setNewCountryId(selectedId);
                  const foundCountry = countries.find(
                    c => c.id.toString() === selectedId.toString()
                  );
                  if (foundCountry && (!newName || countries.some(c => c.name === newName))) {
                    setNewName(foundCountry.name);
                  }
                }}
              >
                <option value="">-- Selecciona un País --</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>
                    {country.name} ({country.abbreviation})
                  </option>
                ))}
              </select>
              <small className="text-muted d-block mt-2" style={{ fontSize: '0.85rem' }}>
                <i className="fa fa-info-circle me-1 text-primary"></i>
                Si el país que buscas no está en la lista, puedes crearlo en la sección de{' '}
                <a href="/admin/countries" target="_blank" rel="noreferrer">
                  Administrar Países
                </a>
                .
              </small>
            </div>
          ) : (
            <div className="form-group mb-3">
              <label className="fw-semibold mb-2">Ciudades que componen el destino</label>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                      Disponibles
                    </span>
                    <span className="badge bg-secondary rounded-pill">
                      {availableCities.length}
                    </span>
                  </div>
                  <div
                    className="border rounded bg-white shadow-sm d-flex flex-column"
                    style={{ height: '260px' }}
                  >
                    <div className="p-2 border-bottom bg-light">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text bg-white border-end-0 text-muted">
                          <i className="fa fa-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          placeholder="Buscar ciudad..."
                          value={citySearch}
                          onChange={e => setCitySearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      <ul className="list-group list-group-flush">
                        {availableCities.map(city => (
                          <li
                            key={city.id}
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3 border-bottom-0 border-top"
                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onClick={() => setSelectedCities([...selectedCities, city.id])}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                          >
                            <div>
                              <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                                {city.name}
                              </div>
                              <small
                                className="text-muted d-block"
                                style={{ fontSize: '0.75rem', marginTop: '-2px' }}
                              >
                                {city.regionName}
                              </small>
                            </div>
                            <i className="fa fa-plus-circle text-primary opacity-50"></i>
                          </li>
                        ))}
                        {availableCities.length === 0 && (
                          <li className="list-group-item text-center text-muted p-4 border-0">
                            <i className="fa fa-search d-block mb-2 text-black-50 fa-2x"></i>
                            <span style={{ fontSize: '0.85rem' }}>No se encontraron ciudades</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2 px-1" style={{ fontSize: '0.8rem' }}>
                    <i className="fa fa-info-circle me-1"></i>Clic para añadir
                  </small>
                </div>

                <div className="col-md-6">
                  <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                    <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                      Seleccionadas
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-primary rounded-pill">{selectedCities.length}</span>
                      {selectedCities.length > 0 && (
                        <button
                          className="btn btn-sm btn-outline-danger py-0 px-2 rounded-pill"
                          style={{ fontSize: '0.75rem', height: '22px' }}
                          onClick={() => setSelectedCities([])}
                        >
                          Limpiar todo
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    className="border rounded bg-white shadow-sm d-flex flex-column"
                    style={{ height: '260px' }}
                  >
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      <ul className="list-group list-group-flush">
                        {selectedCitiesObjects.map(city => (
                          <li
                            key={city.id}
                            className="list-group-item list-group-item-action list-group-item-primary d-flex justify-content-between align-items-center py-2 px-3 border-bottom-0 border-top"
                            style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onClick={() =>
                              setSelectedCities(selectedCities.filter(id => id !== city.id))
                            }
                          >
                            <div>
                              <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                                {city.name}
                              </div>
                              <small
                                className="text-dark opacity-75 d-block"
                                style={{ fontSize: '0.75rem', marginTop: '-2px' }}
                              >
                                {city.regionName}
                              </small>
                            </div>
                            <i className="fa fa-minus-circle text-danger opacity-75"></i>
                          </li>
                        ))}
                        {selectedCitiesObjects.length === 0 && (
                          <li className="list-group-item text-center text-muted p-4 border-0 h-100 d-flex flex-column align-items-center justify-content-center">
                            <i className="fa fa-list d-block mb-2 text-black-50 fa-2x"></i>
                            <span style={{ fontSize: '0.85rem' }}>Ninguna ciudad seleccionada</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2 px-1" style={{ fontSize: '0.8rem' }}>
                    <i className="fa fa-info-circle me-1"></i>Clic para remover
                  </small>
                </div>
              </div>
            </div>
          )}

          <div className="form-group mb-3">
            <label className="fw-semibold mb-1">{texts.admin_common.name}</label>
            <input
              type="text"
              className="form-control"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Ej. Madrid"
            />
            {(newName || (isUpdate && currentSlug)) && (
              <div className="mt-2 p-2 bg-light border rounded d-flex align-items-center gap-2">
                <small className="text-muted fw-semibold">URL Destino:</small>
                <a
                  href={`/page/${
                    isUpdate && currentSlug
                      ? currentSlug
                      : newName
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/[\s/]+/g, '-')
                  }`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-decoration-none small"
                  style={{ wordBreak: 'break-all' }}
                >
                  <i className="fa fa-external-link-alt me-1"></i>
                  /page/
                  {isUpdate && currentSlug
                    ? currentSlug
                    : newName
                        .toLowerCase()
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .replace(/[\s/]+/g, '-')}
                </a>
              </div>
            )}
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <div className="form-group">
                <label className="fw-semibold mb-1">Año</label>
                <input
                  type="number"
                  className="form-control"
                  placeholder="Ej. 2026"
                  value={newYear}
                  onChange={e => setNewYear(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="form-group">
                <label className="fw-semibold mb-2">Semestre</label>
                <div className="d-flex gap-3 mt-1">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sem1"
                      checked={newSemesters.includes('1')}
                      onChange={() => toggleSemester('1')}
                    />
                    <label className="form-check-label" htmlFor="sem1">
                      {texts.wizard?.semester_1_short || 'Sem. 1 (Marzo - Julio)'}
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sem2"
                      checked={newSemesters.includes('2')}
                      onChange={() => toggleSemester('2')}
                    />
                    <label className="form-check-label" htmlFor="sem2">
                      {texts.wizard?.semester_2_short || 'Sem. 2 (Agosto - Diciembre)'}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="form-group mb-2">
            <label className="fw-semibold mb-1">URL Grupo WhatsApp</label>
            <input
              type="text"
              className="form-control"
              placeholder="Ej: https://chat.whatsapp.com/... o https://wa.me/..."
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
            />
            <small
              className="text-muted mt-1 d-block"
              style={{ fontSize: '0.85rem', lineHeight: '1.3' }}
            >
              <i className="fa fa-info-circle me-1 text-primary"></i>
              Si usas un enlace directo de chat (<strong>wa.me</strong> o{' '}
              <strong>api.whatsapp.com</strong>), se incluirá automáticamente el mensaje
              predeterminado <em>"Hola! Bienvenido al GRUPO..."</em>. Si usas un enlace de grupo (
              <strong>chat.whatsapp.com</strong>), WhatsApp solo abrirá la invitación sin el mensaje
              preescrito.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            {texts.admin_common.cancel}
          </Button>
          <Button variant="primary" onClick={isUpdate ? handleUpdate : handleSave}>
            <i className="fa fa-save me-2"></i>
            {isUpdate ? texts.admin_common.update : texts.admin_common.save}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Destinations;
